import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, fmtDateTime, fmtINR, STAGE_LABEL } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, ArrowLeft, Phone, Mail, Globe, MapPin, User } from "lucide-react";
import useSmartBack from "@/hooks/useSmartBack";

function OwnerTag({ name }) {
  if (!name) return null;
  return <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><User className="h-3 w-3" />{name}</span>;
}

export default function AccountDetail() {
  const { id } = useParams();
  const goBack = useSmartBack("/accounts");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/accounts/${id}/summary`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-6"><Skeleton className="h-12 w-1/2 mb-4" /><Skeleton className="h-64 rounded-3xl" /></div>;
  if (!data) return <div className="p-6">Account not found.</div>;

  const c = data.client;
  const history = [
    ...(data.activities || []).map((a) => ({ kind: "Activity", title: a.summary, detail: a.details, owner: a.owner_name, at: a.created_at, id: `a-${a.id}` })),
    ...(data.followups || []).map((f) => ({ kind: "Follow-up", title: f.title, detail: `Due ${fmtDate(f.due_date)} · ${f.status || ""}`, owner: f.owner_name, at: f.created_at, id: `f-${f.id}` })),
    ...(data.meetings || []).map((m) => ({ kind: "Meeting", title: m.title || m.subject || "Meeting", detail: m.location, owner: m.owner_name, at: m.created_at, id: `m-${m.id}` })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto" data-testid="account-detail">
      <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground mb-4 tap" data-testid="back-accounts">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      {/* 1. Company information */}
      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-wrap items-start gap-5">
            <div className="h-16 w-16 rounded-3xl bg-primary-50 text-primary-700 grid place-items-center">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{c.industry || "Customer account"}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">{c.company_name}</h1>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                {(c.location || c.state) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.location || [c.district, c.state].filter(Boolean).join(", ")}</span>}
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
                {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
                {c.website && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{c.website}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                {c.gst_number && <span>GSTIN: <strong className="font-mono">{c.gst_number}</strong></span>}
                {c.owner_name && <span>Account owner: <strong className="text-foreground/80">{c.owner_name}</strong></span>}
              </div>
            </div>
            <Link to={`/clients/${c.id}`} className="text-xs font-semibold underline text-primary-700" data-testid="account-open-client">Open client page →</Link>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="contacts">
        <div className="overflow-x-auto -mx-1 px-1 mb-4">
          <TabsList className="rounded-2xl w-max">
            <TabsTrigger value="contacts" className="rounded-xl" data-testid="account-tab-contacts">Contacts ({(data.contacts || []).length})</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-xl" data-testid="account-tab-leads">Leads ({(data.leads || []).length})</TabsTrigger>
            <TabsTrigger value="quotations" className="rounded-xl" data-testid="account-tab-quotations">Quotations ({(data.quotations || []).length})</TabsTrigger>
            <TabsTrigger value="deals" className="rounded-xl" data-testid="account-tab-deals">Deals ({(data.deals || []).length})</TabsTrigger>
            <TabsTrigger value="history" className="rounded-xl" data-testid="account-tab-history">Activities & History ({history.length})</TabsTrigger>
          </TabsList>
        </div>

        {/* 2. Contact persons */}
        <TabsContent value="contacts">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.contacts || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No contact persons yet.</p>
              ) : data.contacts.map((ct) => (
                <div key={ct.id} className="p-3.5 rounded-2xl border border-border/60" data-testid={`account-contact-${ct.id}`}>
                  <p className="font-semibold text-sm">{ct.name}</p>
                  <p className="text-xs text-muted-foreground">{ct.designation || "—"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{[ct.phone, ct.email].filter(Boolean).join(" · ") || "—"}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. Leads */}
        <TabsContent value="leads">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.leads || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No leads linked to this company.</p>
              ) : data.leads.map((l) => (
                <div key={l.id} className="p-3.5 rounded-2xl border border-border/60 flex items-center justify-between gap-3 flex-wrap" data-testid={`account-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{l.name}</p>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider capitalize">{(l.status || "new").replace(/_/g, " ")}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Created {fmtDate(l.created_at)}</p>
                  </div>
                  <OwnerTag name={l.owner_name} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. Quotations */}
        <TabsContent value="quotations">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.quotations || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No quotations yet.</p>
              ) : data.quotations.map((q) => (
                <Link key={q.id} to={`/quotations/${q.id}`} className="block p-3.5 rounded-2xl border border-border/60 hover:bg-muted/30 tap transition" data-testid={`account-quotation-${q.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono text-xs font-bold tracking-wider">{q.number}</p>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider capitalize">{q.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(q.created_at)} · <OwnerTag name={q.owner_name} /></p>
                    </div>
                    <p className="font-semibold text-sm tabular-nums shrink-0">{fmtINR(q.grand_total)}</p>
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Deals */}
        <TabsContent value="deals">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.deals || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No deals yet.</p>
              ) : data.deals.map((d) => (
                <Link key={d.id} to={`/deals/${d.id}`} className="block p-3.5 rounded-2xl border border-border/60 hover:bg-muted/30 tap transition" data-testid={`account-deal-${d.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{d.title}</p>
                      <p className="text-xs text-muted-foreground capitalize mt-0.5">{STAGE_LABEL[d.stage] || d.stage} · {fmtINR(d.value)}</p>
                    </div>
                    <OwnerTag name={d.owner_name} />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 6. Activities & history */}
        <TabsContent value="history">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-3">
              {history.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No interactions logged yet.</p>
              ) : history.map((h) => (
                <div key={h.id} className="flex gap-3 p-3.5 rounded-2xl border border-border/60" data-testid={`account-history-${h.id}`}>
                  <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 grid place-items-center shrink-0 text-[9px] font-bold uppercase">{h.kind.slice(0, 2)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{h.title}</p>
                      <Badge variant="outline" className="text-[10px]">{h.kind}</Badge>
                    </div>
                    {h.detail && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{h.detail}</p>}
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-1.5">
                      {[h.owner, h.at && fmtDateTime(h.at)].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
